import React, { useState, useEffect } from 'react';
import { api } from '@/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Target, Trash2, DollarSign, TrendingUp } from 'lucide-react';

const SERVICE_TYPES = [
  'Planning', 'Delay Claims', 'Time Impact Analysis', 'Advisory', 
  'Retainer', 'Forensic Analysis', 'Expert Witness', 'Other'
];

const LEAD_SOURCES = ['Referral', 'LinkedIn', 'Website', 'Cold Call', 'Conference', 'Existing Client', 'Other'];

const OpportunityModal = ({ open, opportunity, companies, contacts, stages, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    company_id: '',
    contact_id: '',
    stage: 'Lead Identified',
    service_type: 'Advisory',
    estimated_value: 0,
    probability: 20,
    expected_close_date: '',
    description: '',
    competitor: '',
    risk_factors: '',
    lead_source: '',
    notes: '',
    lost_reason: '',
    tags: [],
  });
  const [loading, setLoading] = useState(false);
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    if (opportunity) {
      setFormData({
        name: opportunity.name || '',
        company_id: opportunity.company_id || '',
        contact_id: opportunity.contact_id || '',
        stage: opportunity.stage || 'Lead Identified',
        service_type: opportunity.service_type || 'Advisory',
        estimated_value: opportunity.estimated_value || 0,
        probability: opportunity.probability || 20,
        expected_close_date: opportunity.expected_close_date || '',
        description: opportunity.description || '',
        competitor: opportunity.competitor || '',
        risk_factors: opportunity.risk_factors || '',
        lead_source: opportunity.lead_source || '',
        notes: opportunity.notes || '',
        lost_reason: opportunity.lost_reason || '',
        tags: opportunity.tags || [],
      });
    } else {
      setFormData({
        name: '',
        company_id: '',
        contact_id: '',
        stage: 'Lead Identified',
        service_type: 'Advisory',
        estimated_value: 0,
        probability: 20,
        expected_close_date: '',
        description: '',
        competitor: '',
        risk_factors: '',
        lead_source: '',
        notes: '',
        lost_reason: '',
        tags: [],
      });
    }
  }, [opportunity, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Deal name is required');
      return;
    }

    setLoading(true);
    try {
      const dataToSend = { ...formData };
      if (!dataToSend.company_id) delete dataToSend.company_id;
      if (!dataToSend.contact_id) delete dataToSend.contact_id;
      if (!dataToSend.expected_close_date) delete dataToSend.expected_close_date;

      if (opportunity?.id) {
        await api.crm.updateOpportunity(opportunity.id, dataToSend);
        toast.success('Deal updated successfully');
      } else {
        await api.crm.createOpportunity(dataToSend);
        toast.success('Deal created successfully');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save deal');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!opportunity?.id) return;
    if (!window.confirm('Are you sure you want to delete this deal?')) return;

    setLoading(true);
    try {
      await api.crm.deleteOpportunity(opportunity.id);
      toast.success('Deal deleted');
      onSave();
      onClose();
    } catch (error) {
      toast.error('Failed to delete deal');
    } finally {
      setLoading(false);
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  const weightedValue = formData.estimated_value * formData.probability / 100;
  const filteredContacts = formData.company_id 
    ? contacts.filter(c => c.company_id === formData.company_id)
    : contacts;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-indigo-600" />
            {opportunity ? 'Edit Deal' : 'New Deal'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Deal Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., ABC Corp - Delay Claims Advisory"
                data-testid="opportunity-name-input"
              />
            </div>

            <div>
              <Label>Company</Label>
              <Select
                value={formData.company_id || '__none__'}
                onValueChange={(value) => setFormData({ ...formData, company_id: value === '__none__' ? '' : value, contact_id: '' })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No company</SelectItem>
                  {companies.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Primary Contact</Label>
              <Select
                value={formData.contact_id || '__none__'}
                onValueChange={(value) => setFormData({ ...formData, contact_id: value === '__none__' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select contact" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No contact</SelectItem>
                  {filteredContacts.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Stage</Label>
              <Select
                value={formData.stage}
                onValueChange={(value) => setFormData({ ...formData, stage: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {stages.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Service Type</Label>
              <Select
                value={formData.service_type}
                onValueChange={(value) => setFormData({ ...formData, service_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_TYPES.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Estimated Value (R)</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="number"
                  value={formData.estimated_value}
                  onChange={(e) => setFormData({ ...formData, estimated_value: parseFloat(e.target.value) || 0 })}
                  className="pl-10"
                  placeholder="0"
                />
              </div>
            </div>

            <div>
              <Label>Expected Close Date</Label>
              <Input
                type="date"
                value={formData.expected_close_date}
                onChange={(e) => setFormData({ ...formData, expected_close_date: e.target.value })}
              />
            </div>

            <div className="col-span-2">
              <Label>Probability: {formData.probability}%</Label>
              <div className="flex items-center gap-4 mt-2">
                <Slider
                  value={[formData.probability]}
                  onValueChange={(value) => setFormData({ ...formData, probability: value[0] })}
                  max={100}
                  step={5}
                  className="flex-1"
                />
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <TrendingUp className="h-4 w-4" />
                  <span>Weighted: R{weightedValue.toLocaleString()}</span>
                </div>
              </div>
            </div>

            <div>
              <Label>Lead Source</Label>
              <Select
                value={formData.lead_source || '__none__'}
                onValueChange={(value) => setFormData({ ...formData, lead_source: value === '__none__' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Not specified</SelectItem>
                  {LEAD_SOURCES.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Competitor</Label>
              <Input
                value={formData.competitor}
                onChange={(e) => setFormData({ ...formData, competitor: e.target.value })}
                placeholder="Who are we competing against?"
              />
            </div>

            <div className="col-span-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe the opportunity and client needs..."
                rows={3}
              />
            </div>

            <div className="col-span-2">
              <Label>Risk Factors</Label>
              <Textarea
                value={formData.risk_factors}
                onChange={(e) => setFormData({ ...formData, risk_factors: e.target.value })}
                placeholder="What could prevent us from winning this deal?"
                rows={2}
              />
            </div>

            {formData.stage === 'Lost' && (
              <div className="col-span-2">
                <Label>Lost Reason</Label>
                <Textarea
                  value={formData.lost_reason}
                  onChange={(e) => setFormData({ ...formData, lost_reason: e.target.value })}
                  placeholder="Why was this deal lost?"
                  rows={2}
                />
              </div>
            )}

            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional notes..."
                rows={2}
              />
            </div>

            <div className="col-span-2">
              <Label>Tags</Label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Add tag..."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                />
                <Button type="button" variant="outline" onClick={addTag}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.tags.map(tag => (
                  <span key={tag} className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-sm">
                    {tag}
                    <button type="button" onClick={() => removeTag(tag)} className="text-slate-400 hover:text-slate-600">&times;</button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="flex justify-between">
            <div>
              {opportunity?.id && (
                <Button type="button" variant="destructive" onClick={handleDelete} disabled={loading}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={loading} data-testid="save-opportunity-btn">
                {loading ? 'Saving...' : (opportunity ? 'Update' : 'Create')}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default OpportunityModal;
